import type { MouseEvent } from "react";
import { Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useToggleHighlight } from "@/hooks/use-highlighted-interactions";
import { cn } from "@/lib/utils";

interface HighlightInteractionButtonProps {
  interactionId: string;
  /** Pass the server-returned flag state from the list data so the icon renders correctly before any mutation. */
  isHighlighted?: boolean;
  /** Needed to invalidate the highlighted-count badge on the animal profile after toggling. */
  animalId?: string;
  variant?: "icon" | "full" | "menu-item";
  className?: string;
}

export function HighlightInteractionButton({
  interactionId,
  isHighlighted: propHighlighted = false,
  animalId,
  variant = "icon",
  className,
}: HighlightInteractionButtonProps) {
  const toggleHighlight = useToggleHighlight(interactionId, animalId);

  // Derive display state: prefer the latest server response, fall back to the prop from list data.
  // Flip optimistically while the mutation is in-flight.
  const baseHighlighted = toggleHighlight.data
    ? (toggleHighlight.data.flagReasons ?? []).some((r) => r.type === "highlighted")
    : propHighlighted;
  const isHighlighted = toggleHighlight.isPending ? !baseHighlighted : baseHighlighted;

  const handleClick = (e?: MouseEvent) => {
    e?.stopPropagation();
    toggleHighlight.mutate();
  };

  if (variant === "menu-item") {
    return (
      <DropdownMenuItem
        onClick={(e) => {
          e.stopPropagation();
          toggleHighlight.mutate();
        }}
        disabled={toggleHighlight.isPending}
        className={className}
        data-testid={`button-highlight-interaction-${interactionId}`}
      >
        <Flag
          className={cn(
            "h-4 w-4 mr-2",
            isHighlighted && "fill-current text-amber-500 dark:text-amber-400"
          )}
        />
        {isHighlighted ? "Remove highlight" : "Highlight"}
      </DropdownMenuItem>
    );
  }

  if (variant === "full") {
    return (
      <Button
        variant="outline"
        onClick={handleClick}
        disabled={toggleHighlight.isPending}
        className={className}
        data-testid={`button-highlight-interaction-${interactionId}`}
      >
        <Flag
          className={cn(
            "h-4 w-4 mr-2",
            isHighlighted && "fill-current text-amber-500 dark:text-amber-400"
          )}
        />
        {isHighlighted ? "Highlighted" : "Highlight"}
      </Button>
    );
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleClick}
      disabled={toggleHighlight.isPending}
      title={isHighlighted ? "Remove highlight" : "Highlight"}
      className={className}
      data-testid={`button-highlight-interaction-${interactionId}`}
    >
      <Flag
        className={cn(
          "h-4 w-4",
          isHighlighted && "fill-current text-amber-500 dark:text-amber-400"
        )}
      />
    </Button>
  );
}
